import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RAG_STRATEGY_AGENT_LED,
  RAG_STRATEGY_LEGACY,
  deriveEnginePath,
  loadToggleOverrides,
  resolveInitialToggles,
  saveToggleOverrides,
} from '../utils/chatEngineRouting';

export function useChatEngineToggles(assistant, assistantId, hasAgentTools, availableCollections) {
  const initRef = useRef(false);
  const [collectionAttached, setCollectionAttached] = useState(false);
  const [toolsEnabled, setToolsEnabled] = useState(false);
  const [ragStrategy, setRagStrategy] = useState(RAG_STRATEGY_LEGACY);
  const [selectedCollection, setSelectedCollection] = useState(null);

  useEffect(() => {
    initRef.current = false;
    setCollectionAttached(false);
    setToolsEnabled(false);
    setRagStrategy(RAG_STRATEGY_LEGACY);
    setSelectedCollection(null);
  }, [assistantId]);

  useEffect(() => {
    if (initRef.current || !assistant) return;
    const overrides = loadToggleOverrides(assistantId);
    const initial = resolveInitialToggles(assistant, hasAgentTools, overrides);
    setCollectionAttached(initial.collectionAttached);
    setToolsEnabled(initial.toolsEnabled);
    setRagStrategy(initial.ragStrategy);
    if (initial.collectionId && availableCollections.some((c) => c.id === initial.collectionId)) {
      setSelectedCollection(initial.collectionId);
    } else if (initial.collectionAttached && availableCollections.length === 1) {
      setSelectedCollection(availableCollections[0].id);
    }
    initRef.current = true;
  }, [assistant, assistantId, hasAgentTools, availableCollections]);

  const persist = useCallback((partial) => {
    saveToggleOverrides(assistantId, partial);
  }, [assistantId]);

  const setCollectionAttachedPersist = useCallback((value) => {
    setCollectionAttached(value);
    persist({ collectionAttached: value });
    if (value && !selectedCollection) {
      const id = assistant?.data_collection_id
        || (availableCollections.length === 1 ? availableCollections[0].id : null);
      if (id) setSelectedCollection(id);
    }
  }, [assistant, availableCollections, persist, selectedCollection]);

  const setToolsEnabledPersist = useCallback((value) => {
    setToolsEnabled(value);
    persist({ toolsEnabled: value });
  }, [persist]);

  const setRagStrategyPersist = useCallback((value) => {
    setRagStrategy(value);
    persist({ ragStrategy: value });
  }, [persist]);

  const setSelectedCollectionPersist = useCallback((value) => {
    setSelectedCollection(value);
    persist({ collectionId: value });
  }, [persist]);

  const toggles = useMemo(() => ({
    collectionAttached,
    toolsEnabled,
    ragStrategy,
    collectionId: selectedCollection,
  }), [collectionAttached, toolsEnabled, ragStrategy, selectedCollection]);

  const enginePath = useMemo(
    () => deriveEnginePath(toggles),
    [toggles],
  );

  const showRagStrategy = collectionAttached && toolsEnabled && hasAgentTools;

  return {
    collectionAttached,
    setCollectionAttached: setCollectionAttachedPersist,
    toolsEnabled,
    setToolsEnabled: setToolsEnabledPersist,
    ragStrategy,
    setRagStrategy: setRagStrategyPersist,
    selectedCollection,
    setSelectedCollection: setSelectedCollectionPersist,
    enginePath,
    showRagStrategy,
    toggles,
  };
}

export default useChatEngineToggles;
